import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbTimepicker, PlacementArray } from '@ng-bootstrap/ng-bootstrap';
import { IMaskDirective } from 'angular-imask';
import IMask from 'imask';
import moment from 'moment';
import { TIME_12_FORMAT_WITHOUT_SECONDS_REGEXP } from '../../utils/constants';
import { InputErrorsComponent, InputLabelComponent, InputPreAppendComponent, MaskedOptions, MaskElement } from '../index';
import { InputBase } from '../input-base/input-base';
import { DOMEvent, InputClasses } from '../input-base/input-base.model';
/**
 * Component that provides a timepicker. Based on
 * <a href="https://ng-bootstrap.github.io/#/components/timepicker/examples" target="_blank">NgbTimepicker</a>
 * and <a href="https://ng-bootstrap.github.io/#/components/dropdown/examples" target="_blank">NgbDropdown</a>
 */
@Component({
  selector: 'app-timepicker,mbs-timepicker',
  templateUrl: 'timepicker.component.html',
  imports: [
    FormsModule,
    IMaskDirective,
    InputErrorsComponent,
    InputLabelComponent,
    InputPreAppendComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbTimepicker,
    NgClass
  ],
  host: {
    class: 'mbs-timepicker'
  }
})
export class TimepickerComponent extends InputBase<string> implements OnInit, OnDestroy, ControlValueAccessor {
  protected cd: ChangeDetectorRef;

  /**
   * @private time formats for moment.js
   */
  private lastValidState = '12:00 AM';
  private readonly momentFormat12 = 'hh:mm A';
  private readonly momentFormat24 = 'HH:mm';
  private readonly testRegExp = new RegExp(TIME_12_FORMAT_WITHOUT_SECONDS_REGEXP);
  protected labelContentClassesDefault = 'mbs-timepicker_label-content';

  @Input() imaskOptions?: MaskedOptions;
  @Input() unmask?: boolean | 'typed';
  @Input() imaskElement: (elementRef: ElementRef<MaskElement>, directiveRef: any) => MaskElement = (elementRef) => elementRef.nativeElement;
  @ViewChild(NgbDropdown, { static: false }) dropdown: NgbDropdown;
  /**
   * Whether to display 12H or 24H mode. 12H if `true`. <br>
   * Input always displays value in 24H mode.
   */
  @Input() meridian = false;
  /**
   * If `true`, it is possible to select seconds
   */
  @Input() showSeconds = false;
  /**
   *  CSS-Class to customize dropdown
   */
  @Input() dropdownClass: string;
  /**
   * The preferred placement of the dropdown.
   * See details in NgBootstrap <a href="https://ng-bootstrap.github.io/#/components/dropdown/api" target="_blank">docs</a>
   */
  @Input() placement: PlacementArray = 'bottom-left';
  /**
   * If `true`, the spinners above and below inputs are visible. (only for dropdown)
   */
  @Input() spinners = true;
  /**
   * The number of hours to add/subtract when clicking hours spinners.
   */
  @Input() hourStep = 1;
  /**
   * The number of minutes to add/subtract when clicking minutes spinners.
   */
  @Input() minuteStep = 1;
  /**
   * The number of seconds to add/subtract when clicking seconds spinners.
   */
  @Input() secondStep = 1;
  /**
   * Show clear button
   */
  @Input() clearButton = false;
  /**
   * Show spinner
   */
  @Input() loading = false;
  /**
   * if [isErrorsShow]="false" we hidden mbs-input-errors
   */
  @Input() isShowErrors = true;

  @Input() needChangeInvalid = false;
  /**
   * Add font-weight-bold;
   */
  @Input() public boldLabel = true;
  /**
   * @ignore
   */
  @Output() change = new EventEmitter();
  /**
   * @ignore
   */
  @Output() buttonClickPrepend = new EventEmitter();
  /**
   * @ignore
   */
  @Output() buttonClickAppend = new EventEmitter();

  /**
   * dynamic setting converted value in form
   * @param {string}time
   */
  public set value(time: string) {
    const isValid = this.testRegExp.test(time);

    this.myValue = isValid ? time : this.needChangeInvalid ? this.lastValidState : time;

    if (isValid && this.needChangeInvalid) {
      this.lastValidState = time;
    }

    const newTime = this.convertTime(this.myValue, this.momentFormat12, this.momentFormat24);

    this.onChange?.(newTime);
    this.cd.markForCheck();
    this.change.emit(newTime);
  }

  /**
   * get converted time from input
   */
  public get value(): string {
    return this.myValue;
  }

  /**
   * @ignore
   */
  public get bindClasses(): string[] {
    const classesObject: InputClasses = {
      '-clearable': this.clearButton || this.loading,
      ...this.validClasses
    };

    return Object.entries(classesObject)
      .filter(([k, v]) => !!v)
      .map(([k]) => k)
      .concat([this.sizeClass]);
  }

  constructor() {
    const ngControl = inject(NgControl, { optional: true, self: true });
    const cd = inject(ChangeDetectorRef);

    super();
    this.cd = cd;

    /**
     * The options of imask time format.
     * See details here https://imask.js.org/guide.html#masked-base
     */
    this.imaskOptions = {
      mask: this.momentFormat12,
      overwrite: true,
      autofix: true,
      lazy: false,
      blocks: {
        HH: {
          mask: IMask.MaskedRange,
          placeholderChar: '_',
          from: 0,
          to: 23,
          maxLength: 2
        } as IMask.AnyMaskedOptions,
        hh: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
          placeholderChar: '_'
        } as IMask.AnyMaskedOptions,
        mm: {
          mask: IMask.MaskedRange,
          placeholderChar: '_',
          from: 0,
          to: 59,
          maxLength: 2
        } as IMask.AnyMaskedOptions,
        A: {
          mask: IMask.MaskedEnum,
          enum: ['AM', 'am', 'PM', 'pm', 'aM', 'Am', 'pM', 'Pm']
        } as IMask.AnyMaskedOptions
      }
    };
  }

  ngOnInit(): void {
    if (this.label && !this.id) {
      console.error('Timepicker requires Id if label in use');
    }
  }

  ngOnDestroy(): void {
    this.dropdown?.ngOnDestroy();
  }

  timePickerBlurHandler(event: DOMEvent<HTMLInputElement>): void {
    if (this.needChangeInvalid) {
      this.value = event.target.value = this.lastValidState;
    }

    this.handleBlur(event);
  }

  /**
   * first time setting converted value in initialized input
   * @param {string}time
   */
  writeValue(time: string): void {
    const newTime = time == null ? null : this.convertTime(time, this.momentFormat24, this.momentFormat12);

    super.writeValue(newTime);

    if (newTime && this.needChangeInvalid && this.testRegExp.test(newTime)) {
      Promise.resolve().then(() => (this.lastValidState = newTime));
    }
  }

  /**
   * Convert time from current format to another format using moment.js
   * @param {string} time - current time
   * @param {string} from - current time format
   * @param {to} to - format in which the time will be converted
   * @return {string}
   */
  convertTime(time: string, from: string, to: string): string {
    return moment(time, from, false).format(to);
  }

  handleChange(event: DOMEvent<HTMLInputElement>): void {
    // replacing extra symbol from imask and making in upperCase am/pm to AM/PM
    this.value = event.target.value.replace('_', '').toUpperCase();
  }
}
